import { useEffect, useState } from 'react'
import { Search, ChevronDown, Award, TrendingUp, Loader2, RefreshCw } from 'lucide-react'
import { resultsApi, examsApi, classesApi } from '../api/services.js'
import toast from 'react-hot-toast'

function getGrade(pct) {
  if (pct >= 90) return { label: 'A+', color: 'text-green-700 bg-green-50' }
  if (pct >= 80) return { label: 'A', color: 'text-blue-700 bg-blue-50' }
  if (pct >= 70) return { label: 'B', color: 'text-purple-700 bg-purple-50' }
  if (pct >= 60) return { label: 'C', color: 'text-yellow-700 bg-yellow-50' }
  if (pct >= 50) return { label: 'D', color: 'text-orange-700 bg-orange-50' }
  return { label: 'F', color: 'text-red-700 bg-red-50' }
}

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {[...Array(5)].map((_, i) => <td key={i} className="table-td"><div className="h-3 bg-gray-100 rounded w-3/4" /></td>)}
    </tr>
  )
}

export default function ResultsPage() {
  const [results, setResults] = useState([])
  const [exams, setExams] = useState([])
  const [classes, setClasses] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterClass, setFilterClass] = useState('')
  const [filterExam, setFilterExam] = useState('')

  const fetchData = async () => {
    try {
      setLoading(true)
      const [resultsData, examsData, classesData] = await Promise.allSettled([
        resultsApi.getAll({ exam: filterExam || undefined }),
        examsApi.getAll({ status: 'Completed' }),
        classesApi.getAll(),
      ])
      if (resultsData.status === 'fulfilled') {
        setResults(Array.isArray(resultsData.value) ? resultsData.value : resultsData.value?.results ?? [])
      }
      if (examsData.status === 'fulfilled') {
        const list = Array.isArray(examsData.value) ? examsData.value : examsData.value?.exams ?? []
        setExams(list)
        if (!filterExam && list.length > 0) setFilterExam(list[0]._id || list[0].id || list[0].name)
      }
      if (classesData.status === 'fulfilled') {
        setClasses(Array.isArray(classesData.value) ? classesData.value : classesData.value?.classes ?? [])
      }
    } catch {
      toast.error('Failed to load results')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchData() }, [filterExam, filterClass])

  const filtered = results.filter((r) => {
    const studentRef = r.studentId || r.student
    const name = (typeof r.studentName === 'string' ? r.studentName : null) || studentRef?.name || `${studentRef?.firstName || ''} ${studentRef?.lastName || ''}`.trim() || ''
    const matchSearch = name.toLowerCase().includes(search.toLowerCase()) || (r.roll || r.admissionNo || studentRef?.admissionNo || '').toLowerCase().includes(search.toLowerCase())
    const rClassId = (typeof r.classId === 'object' ? r.classId?._id : r.classId) || (typeof r.class === 'object' ? r.class?._id : null)
    const matchClass = !filterClass || rClassId === filterClass
    return matchSearch && matchClass
  })

  // Normalise: results may come with different shapes from the API
  const getMarks = (r) => {
    if (typeof r.percentage === 'number') return r.percentage
    if (r.marks && r.maxMarks) return Math.round((r.marks / r.maxMarks) * 100)
    if (Array.isArray(r.subjects)) {
      const total = r.subjects.reduce((s, sub) => s + (sub.marks || 0), 0)
      const max = r.subjects.reduce((s, sub) => s + (sub.maxMarks || 100), 0)
      return max ? Math.round((total / max) * 100) : 0
    }
    return r.percentage ?? 0
  }

  const avgPercent = filtered.length ? Math.round(filtered.reduce((sum, r) => sum + getMarks(r), 0) / filtered.length) : 0
  const passCount = filtered.filter((r) => getMarks(r) >= 50).length
  const topScorer = filtered.length ? filtered.reduce((top, r) => getMarks(r) > getMarks(top) ? r : top, filtered[0]) : null

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Results</h2>
          <p className="text-sm text-gray-500 mt-0.5">View and analyse student exam performance</p>
        </div>
        <button onClick={fetchData} className="btn-secondary p-2 self-start sm:self-auto" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {/* Summary */}
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Class Average', value: `${avgPercent}%`, icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Pass Rate', value: `${filtered.length ? Math.round(passCount / filtered.length * 100) : 0}%`, icon: Award, color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Top Score', value: topScorer ? `${getMarks(topScorer)}%` : '—', icon: Award, color: 'text-purple-600', bg: 'bg-purple-50' },
            { label: 'Students', value: filtered.length, icon: TrendingUp, color: 'text-orange-600', bg: 'bg-orange-50' },
          ].map((s) => {
            const Icon = s.icon
            return (
              <div key={s.label} className="card p-4">
                <div className={`w-9 h-9 rounded-xl ${s.bg} flex items-center justify-center mb-3`}><Icon className={`w-4 h-4 ${s.color}`} /></div>
                <p className={`text-2xl font-extrabold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
              </div>
            )
          })}
        </div>
      )}

      {/* Filters */}
      <div className="card p-4 flex flex-col sm:flex-row gap-3">
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 flex-1">
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search student..." className="bg-transparent text-sm text-gray-700 placeholder-gray-400 outline-none w-full" />
        </div>
        <div className="relative">
          <select value={filterClass} onChange={(e) => setFilterClass(e.target.value)} className="input-field pr-8 appearance-none cursor-pointer">
            <option value="">All Classes</option>
            {classes.map((c) => <option key={c._id || c.id} value={c._id || c.id}>{c.name}{c.section ? ` — ${c.section}` : ''}</option>)}
          </select>
          <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        {exams.length > 0 && (
          <div className="relative">
            <select value={filterExam} onChange={(e) => setFilterExam(e.target.value)} className="input-field pr-8 appearance-none cursor-pointer">
              {exams.map((e) => <option key={e._id || e.id || e.name} value={e._id || e.id || e.name}>{e.name || e.subject}</option>)}
            </select>
            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        )}
      </div>

      {/* Results Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="table-th">Student</th>
                <th className="table-th hidden sm:table-cell">Class</th>
                <th className="table-th hidden md:table-cell">Marks</th>
                <th className="table-th">Percentage</th>
                <th className="table-th">Grade</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                [...Array(5)].map((_, i) => <SkeletonRow key={i} />)
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-10 text-gray-400 text-sm">No results found</td></tr>
              ) : filtered.map((r) => {
                const pct = getMarks(r)
                const grade = getGrade(pct)
                const studentRef = r.studentId || r.student
                const name = (typeof r.studentName === 'string' ? r.studentName : null) || (typeof r.name === 'string' ? r.name : null) || studentRef?.name || `${studentRef?.firstName || ''} ${studentRef?.lastName || ''}`.trim() || 'Unknown'
                const marks = r.marks != null ? `${r.marks}/${r.maxMarks}` : (r.subjects ? `${r.subjects.reduce((s, sub) => s + sub.marks, 0)}` : '—')
                return (
                  <tr key={r._id || r.id} className="hover:bg-gray-50 transition-colors">
                    <td className="table-td">
                      <p className="font-medium text-gray-900">{name}</p>
                      <p className="text-xs text-gray-400">{r.roll}</p>
                    </td>
                    <td className="table-td hidden sm:table-cell">
                      <span className="bg-blue-50 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">{typeof r.class === 'object' ? (r.class?.name || '—') : (r.class || r.classId?.name || '—')}</span>
                    </td>
                    <td className="table-td hidden md:table-cell text-gray-500">{marks}</td>
                    <td className="table-td">
                      <div className="flex items-center gap-1.5">
                        <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-blue-500' : pct >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-sm font-medium text-gray-700">{pct}%</span>
                      </div>
                    </td>
                    <td className="table-td">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${grade.color}`}>{grade.label}</span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
